import {
    asFullSourceTransform,
    asSessionTransform,
    type ImageCropRect,
    type ImageEditorScale,
    type ImageSize,
    type PersistedImageEditorTransform,
    type SessionImageEditorTransform,
} from './imageModalTransformTypes';

/** Multiplies two normalised editor scale values without widening the result type. */
const multiplyImageEditorScales = (first: ImageEditorScale, second: ImageEditorScale): ImageEditorScale =>
    first * second === -1 ? -1 : 1;

/** Returns the full-source canvas size after its persisted rotation. */
const getCanvasSizeAfterRotation = (imageSize: ImageSize, rotation: number): ImageSize =>
    Math.abs(rotation) % 180 === 90 ? { width: imageSize.height, height: imageSize.width } : imageSize;

/** Normalises a rotation to the editor's zero-or-negative-quarter-turn convention. */
export const normaliseImageEditorRotation = (rotation: number): number => {
    const positiveRotation = ((rotation % 360) + 360) % 360;
    return positiveRotation === 0 ? 0 : positiveRotation - 360;
};

/** Remaps a crop rectangle into the canvas produced by a session rotation. */
export const rotateImageCropRect = (crop: ImageCropRect, canvas: ImageSize, sessionRotation: number): ImageCropRect => {
    const { x, y, width, height } = crop;

    switch (normaliseImageEditorRotation(sessionRotation)) {
        case -90:
            return { x: y, y: canvas.width - x - width, width: height, height: width };
        case -180:
            return {
                x: canvas.width - x - width,
                y: canvas.height - y - height,
                width,
                height,
            };
        case -270:
            return { x: canvas.height - y - height, y: x, width: height, height: width };
        default:
            return crop;
    }
};

/** Mirrors a crop rectangle across the requested axes of its displayed canvas. */
export const flipImageCropRect = (
    crop: ImageCropRect,
    canvas: ImageSize,
    scaleX: 1 | -1,
    scaleY: 1 | -1,
): ImageCropRect => ({
    ...crop,
    x: scaleX === -1 ? canvas.width - crop.x - crop.width : crop.x,
    y: scaleY === -1 ? canvas.height - crop.y - crop.height : crop.y,
});

/** Composes edits made on an edited image with the transform persisted against its full source. */
export const composeImageEditorTransforms = (
    storedTransform: PersistedImageEditorTransform | null,
    sessionTransform: SessionImageEditorTransform,
    originalImageSize: ImageSize,
): PersistedImageEditorTransform => {
    // With nothing persisted yet, the session edit is itself the full-source absolute transform.
    if (!storedTransform) return asFullSourceTransform(sessionTransform);

    const storedCanvas = getCanvasSizeAfterRotation(originalImageSize, storedTransform.rotation);
    const flippedCrop = flipImageCropRect(
        storedTransform.crop,
        storedCanvas,
        sessionTransform.scaleX,
        sessionTransform.scaleY,
    );
    const isStoredRotatedOnSide = Math.abs(storedTransform.rotation) % 180 === 90;
    const appliedSessionScaleX = isStoredRotatedOnSide ? sessionTransform.scaleY : sessionTransform.scaleX;
    const appliedSessionScaleY = isStoredRotatedOnSide ? sessionTransform.scaleX : sessionTransform.scaleY;

    return asFullSourceTransform({
        crop: rotateImageCropRect(flippedCrop, storedCanvas, sessionTransform.rotation),
        rotation: normaliseImageEditorRotation(storedTransform.rotation + sessionTransform.rotation),
        scaleX: multiplyImageEditorScales(storedTransform.scaleX, appliedSessionScaleX),
        scaleY: multiplyImageEditorScales(storedTransform.scaleY, appliedSessionScaleY),
    });
};

/** Decomposes a full-source transform into the session transform displayed over an edited image. */
export const decomposeImageEditorTransformForEditedImage = (
    storedTransform: PersistedImageEditorTransform | null,
    absoluteTransform: PersistedImageEditorTransform,
    editedImageSize: ImageSize,
): SessionImageEditorTransform => {
    // With nothing persisted yet, the absolute transform is itself the edited-image-relative session.
    if (!storedTransform) return asSessionTransform(absoluteTransform);

    const rotation = normaliseImageEditorRotation(absoluteTransform.rotation - storedTransform.rotation);
    const appliedScaleX = multiplyImageEditorScales(absoluteTransform.scaleX, storedTransform.scaleX);
    const appliedScaleY = multiplyImageEditorScales(absoluteTransform.scaleY, storedTransform.scaleY);
    const isStoredRotatedOnSide = Math.abs(storedTransform.rotation) % 180 === 90;
    const rotatedEditedImageSize = getCanvasSizeAfterRotation(editedImageSize, rotation);

    return asSessionTransform({
        crop: { x: 0, y: 0, ...rotatedEditedImageSize },
        rotation,
        scaleX: isStoredRotatedOnSide ? appliedScaleY : appliedScaleX,
        scaleY: isStoredRotatedOnSide ? appliedScaleX : appliedScaleY,
    });
};
